import React from 'react';
import { FaHammer, FaChartLine, FaCheckCircle } from 'react-icons/fa';
import { useDolphin } from '../../hooks/useDolphin';
import '../../styles/global.css';

function MiningControl() {
  const { stats, endMining, pauseContract, unpauseContract, txLoading } = useDolphin();
  const [error, setError] = React.useState(null);
  const [confirmAction, setConfirmAction] = React.useState(null);
  const [confirmStep, setConfirmStep] = React.useState(0);
  
  const handleAdminAction = async (actionFn, label) => {
    setError(null);
    setConfirmAction(() => actionFn);
    setConfirmStep(1);
  };

  const handleConfirm = async () => {
    if (confirmStep === 1) {
      setConfirmStep(2);
    } else if (confirmStep === 2 && confirmAction) {
      setError(null);
      try {
        await confirmAction();
        setConfirmAction(null);
        setConfirmStep(0);
      } catch (err) {
        setError(err.message || 'Action failed');
        setConfirmAction(null);
        setConfirmStep(0);
      }
    }
  };

  const handleCancel = () => {
    setConfirmAction(null);
    setConfirmStep(0);
  };

  return (
    <div style={{
      background: 'linear-gradient(120deg, #23263a 80%, #38bdf8 120%)',
      borderRadius: 18,
      boxShadow: '0 4px 32px #38bdf855, 0 1.5px 12px #a78bfa33',
      padding: '2.2rem 2.5rem',
      margin: '2rem auto',
      maxWidth: 540,
      color: '#fff',
      position: 'relative',
      overflow: 'hidden',
      border: '1.5px solid #38bdf8',
    }}>
      <div style={{display:'flex',alignItems:'center',gap:16,marginBottom:18}}>
        <span style={{background:'#38bdf8',color:'#23263a',borderRadius:'50%',padding:10,boxShadow:'0 2px 8px #38bdf855'}}>
          <FaHammer size={22}/>
        </span>
        <span style={{fontWeight:800,fontSize:'1.25rem',letterSpacing:'0.01em'}}>Mining Control</span>
      </div>
      <div style={{fontWeight:700,fontSize:'1.08rem',marginBottom:10}}>
        Status: <span style={{color: stats.miningEnded ? '#A78BFA' : '#16a34a',fontWeight:800}}><FaCheckCircle style={{marginRight:4}}/>{stats.miningEnded ? 'Ended' : 'Active'}</span>
      </div>
      <div style={{margin:'1.2rem 0 1.5rem 0',height:10,background:'#181a2a',borderRadius:8,overflow:'hidden',boxShadow:'0 1px 6px #23263a22'}}>
        <div style={{height:'100%',background:'linear-gradient(90deg,#a78bfa 10%,#38bdf8 90%)',width:`${stats.emissionRate ? Math.min(stats.emissionRate/100,100) : 0}%`,transition:'width 0.4s'}}></div>
      </div>
      <div style={{display:'flex',justifyContent:'space-between',fontSize:'0.98rem',marginBottom:18}}>
        <span>Emission Rate: <b style={{color:'#38bdf8'}}>{stats.emissionRate ? stats.emissionRate : '--'}</b></span>
        <span>Max Supply: <b style={{color:'#a78bfa'}}>{stats.totalSupply ? Number(stats.totalSupply).toLocaleString(undefined, {maximumFractionDigits: 0}) : '--'}</b></span>
      </div>
      <div style={{display:'flex',gap:'1.2rem',alignItems:'center',marginBottom:18}}>
        <button 
          className="btn btn-danger" 
          style={{margin:0,padding:'0.7rem 1.5rem',fontWeight:700,fontSize:'1.08rem',borderRadius:10,boxShadow:'0 2px 8px #a78bfa33'}}
          disabled={txLoading || stats.miningEnded}
          onClick={() => handleAdminAction(endMining, 'End Mining')}
        >
          {txLoading ? 'Processing...' : stats.miningEnded ? 'Mining Ended' : 'End Mining'}
        </button>
        <button
          className="btn btn-warning"
          style={{margin:0,padding:'0.7rem 1.5rem',fontWeight:700,fontSize:'1.08rem',borderRadius:10,boxShadow:'0 2px 8px #a78bfa33'}}
          disabled={txLoading}
          onClick={() => handleAdminAction(pauseContract, 'Pause Contract')}
        >
          Pause Contract
        </button>
        <button
          className="btn btn-success"
          style={{margin:0,padding:'0.7rem 1.5rem',fontWeight:700,fontSize:'1.08rem',borderRadius:10,boxShadow:'0 2px 8px #a78bfa33'}}
          disabled={txLoading}
          onClick={() => handleAdminAction(unpauseContract, 'Unpause Contract')}
        >
          Unpause Contract
        </button>
      </div>
      <div style={{display:'flex',gap:'1.5rem',fontSize:'0.98rem',marginBottom:10}}>
        <span style={{color:'#A78BFA'}}><FaChartLine style={{marginRight:4}}/> Emission: {stats.emissionRate ? stats.emissionRate : '--'}</span>
        <span style={{color:'#A78BFA'}}><FaChartLine style={{marginRight:4}}/> Max: {stats.totalSupply ? Number(stats.totalSupply).toLocaleString(undefined, {maximumFractionDigits: 0}) : '--'}</span>
      </div>
      {confirmStep > 0 && (
        <div style={{marginTop:'1.5rem',padding:'1.3rem',background:'linear-gradient(120deg,#23263a 80%,#38bdf8 120%)',borderRadius:12,color:'#fff',fontWeight:700,boxShadow:'0 2px 12px #38bdf855'}}>
          {confirmStep === 1 ? (
            <>
              Are you sure you want to perform this admin action?<br/>
              <button className="btn btn-danger" style={{marginRight:14,marginTop:10,padding:'0.6rem 1.3rem',borderRadius:8}} onClick={handleConfirm}>Yes, Continue</button>
              <button className="btn btn-secondary" style={{marginTop:10,padding:'0.6rem 1.3rem',borderRadius:8}} onClick={handleCancel}>Cancel</button>
            </>
          ) : (
            <>
              Please confirm again to proceed.<br/>
              <button className="btn btn-danger" style={{marginRight:14,marginTop:10,padding:'0.6rem 1.3rem',borderRadius:8}} onClick={handleConfirm}>Yes, I'm Sure</button>
              <button className="btn btn-secondary" style={{marginTop:10,padding:'0.6rem 1.3rem',borderRadius:8}} onClick={handleCancel}>Cancel</button>
            </>
          )}
        </div>
      )}
      {error && <div style={{color:'#ef4444',marginTop:'0.9rem',fontWeight:600,fontSize:'1.05rem'}}>{error}</div>}
    </div>
  );
}
export default MiningControl;
